import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { readUtms, trackConversion } from "@/lib/track";
import { useSettings } from "@/lib/settings";
import MetaPixel from "@/components/MetaPixel";
import {
  ArrowRight, Buildings, ChatCircleText, CheckCircle, Circle,
  Compass, Globe, HandPointing, Shield, WhatsappLogo,
} from "@phosphor-icons/react";

const SERVICES = [
  "Canadian company incorporation",
  "Registered / business address support",
  "Virtual office",
  "Canadian business phone services",
  "Business banking guidance",
  "Administrative support",
  "Business development support",
  "Ongoing Canadian business support",
];

const QUALIFICATION_SERVICES = [
  "Company incorporation", "Virtual office", "Business address",
  "Banking guidance", "Phone service", "Business development", "Local support", "Other",
];

const STEPS_HOW = [
  { n: 1, title: "Tell Us About Your Business", desc: "Share your goals and current situation." },
  { n: 2, title: "Choose Your Setup", desc: "Pick the services that match your plan." },
  { n: 3, title: "We Handle the Process", desc: "We coordinate incorporation and setup end to end." },
  { n: 4, title: "Start Operating Your Canadian Business", desc: "Begin trading with professional support." },
];

export default function CanadaIndiaLanding() {
  const settings = useSettings();
  const navigate = useNavigate();
  const whatsappNumber = (settings.whatsapp_number || "+18253658805").replace(/\D/g, "");

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: "", email: "", whatsapp: "", city_state: "",
    current_occupation: "", intent: "", timeline: "", budget_range: "",
    service_interest: [], marketing_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { trackConversion("lp_canada_india_visit", { value: 1 }); }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      service_interest: f.service_interest.includes(s)
        ? f.service_interest.filter((x) => x !== s)
        : [...f.service_interest, s],
    }));

  const canGoStep2 = form.full_name && form.email && form.whatsapp && form.city_state;
  const canGoStep3 = form.intent && form.timeline && form.budget_range;

  async function submit() {
    setError("");
    if (!form.full_name || !form.email || !form.whatsapp) {
      setError("Please provide your name, email and WhatsApp number.");
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/campaign-leads", {
        ...form,
        campaign: "canada-company-india",
        lead_source: "canada-company-india",
        utm: readUtms() || {},
      });
      trackConversion("lead", { value: 25, payload: { campaign: "canada-company-india" } });
      navigate(
        `/canada-company-india/thank-you?ref=${encodeURIComponent(r.data.reference)}&status=${encodeURIComponent(r.data.lead_status || "")}`,
        { state: { reference: r.data.reference, status: r.data.lead_status } },
      );
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fade-in bg-gos-bone" data-testid="lp-canada-india">
      {/* Meta Pixel — SCOPED to this landing page family only */}
      <MetaPixel />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gos-border bg-white">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="container-gos py-16 md:py-20 relative grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3">
            <div className="overline mb-5 flex items-center gap-2">
              <span className="text-base leading-none">🇮🇳</span>
              <span>For entrepreneurs in India</span>
              <span className="text-gos-sand">·</span>
              <span className="text-base leading-none">🇨🇦</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-gos-charcoal tracking-tight leading-[1.05]" data-testid="lp-headline">
              Start Your <span className="italic text-gos-navy">Canadian Company</span> From India.
            </h1>
            <p className="mt-6 text-base lg:text-lg text-gos-slate leading-relaxed max-w-xl">
              From incorporation to Canadian business address, virtual office and ongoing
              local support, we help you set up and operate your Canadian business remotely
              from India.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 relative">
              <button
                onClick={() => { document.getElementById("qualification-form")?.scrollIntoView({ behavior: "smooth" }); }}
                className="btn-primary relative shadow-[0_0_0_3px_rgba(212,167,79,0.35)]"
                data-testid="lp-cta-eligibility"
              >
                <HandPointing size={16} weight="bold" /> Get My Canada Setup Plan
                <span aria-hidden className="absolute inset-0 rounded-sm animate-pulse-glow pointer-events-none" />
              </button>
              <Link to="/contact?country=canada&source=lp-india" className="btn-secondary" data-testid="lp-cta-consult">
                <ChatCircleText size={16} weight="bold" /> Book a Free Consultation
              </Link>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent("Hi OBA, I'm interested in setting up a Canadian company from India.")}`}
                target="_blank" rel="noreferrer"
                onClick={() => trackConversion("whatsapp_click", { value: 10 })}
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-4 py-2.5 rounded-sm text-sm hover:brightness-110 transition"
                data-testid="lp-cta-whatsapp"
              >
                <WhatsappLogo size={18} weight="fill" /> WhatsApp Us
              </a>
            </div>
            <p className="mt-3 text-xs text-gos-muted" data-testid="lp-cta-microcopy">
              Free initial consultation • No obligation • Canadian-based support
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-xl">
              {["Non-resident friendly", "Canadian-based team", "Professional partner network", "Transparent pricing"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-gos-slate">
                  <CheckCircle size={14} weight="duotone" className="text-gos-success shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Right stat card */}
          <div className="lg:col-span-2">
            <div className="bg-gos-bone border border-gos-border rounded-sm p-6 md:p-7">
              <div className="overline mb-3">Why Canada?</div>
              <div className="space-y-4">
                {[
                  { icon: Globe, title: "G7 economy", desc: "Trusted international jurisdiction with developed markets." },
                  { icon: Buildings, title: "Non-resident setup", desc: "Open a company without relocating — remotely from India." },
                  { icon: Shield, title: "Stable regulation", desc: "Predictable regulatory environment for cross-border trade." },
                ].map((f) => (
                  <div key={f.title} className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-gos-navy text-gos-cream rounded-sm shrink-0">
                      <f.icon size={16} weight="duotone" />
                    </div>
                    <div>
                      <div className="font-display text-sm text-gos-navy">{f.title}</div>
                      <div className="text-xs text-gos-slate mt-0.5 leading-relaxed">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-white border-b border-gos-border" data-testid="lp-how">
        <div className="container-gos">
          <div className="overline mb-3">How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">A simple 4-step journey.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-5">
            {STEPS_HOW.map((s, i) => (
              <div key={s.n} className="bg-gos-bone border border-gos-border rounded-sm p-6 relative">
                <div className="font-display text-4xl text-gos-maple leading-none">{s.n}.</div>
                <h3 className="font-display text-lg text-gos-navy mt-3 leading-tight">{s.title}</h3>
                <p className="text-sm text-gos-slate mt-2 leading-relaxed">{s.desc}</p>
                {i < STEPS_HOW.length - 1 && (
                  <ArrowRight size={18} weight="bold" className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-gos-sand" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-gos-bone border-b border-gos-border" data-testid="lp-services">
        <div className="container-gos">
          <div className="overline mb-3">What's included</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">Services available for your Canadian business.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s) => (
              <div key={s} className="bg-white border border-gos-border rounded-sm p-5 flex items-start gap-3">
                <CheckCircle size={18} weight="fill" className="text-gos-maple shrink-0 mt-0.5" />
                <div className="text-sm text-gos-charcoal leading-relaxed">{s}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gos-muted italic mt-6 max-w-3xl leading-relaxed">
            We do not guarantee bank-account approval, immigration or visa outcomes, tax results
            or any government or third-party approvals. All such decisions remain with the
            applicable authority or provider.
          </p>
        </div>
      </section>

      {/* WHY OBA */}
      <section className="py-20 bg-white border-b border-gos-border" data-testid="lp-why">
        <div className="container-gos grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="overline mb-3">Why Open Business Anywhere</div>
            <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">
              A Canadian team that <span className="italic text-gos-navy">helps international founders go global.</span>
            </h2>
            <p className="text-gos-slate mt-5 leading-relaxed">
              OBA helps entrepreneurs and businesses establish and operate across borders —
              company formation, business presence, banking introductions and ongoing
              support — all coordinated through a single platform and a growing network of
              trusted local partners.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["🇨🇦", "Canadian-based", "Team on the ground in Canada"],
              ["🌐", "Global network", "Partners across multiple markets"],
              ["🤝", "Non-resident friendly", "Remote-first setup from India"],
              ["📞", "Dedicated advisor", "A real human assigned to your file"],
            ].map(([emoji, title, desc]) => (
              <div key={title} className="bg-gos-bone border border-gos-border rounded-sm p-5">
                <div className="text-2xl mb-2">{emoji}</div>
                <div className="font-display text-base text-gos-navy">{title}</div>
                <div className="text-xs text-gos-slate mt-1 leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUALIFICATION FORM */}
      <section id="qualification-form" className="py-20 bg-gos-bone border-b border-gos-border" data-testid="lp-form">
        <div className="container-gos max-w-3xl">
          <div className="overline mb-3">Qualification</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">Tell us about your Canada business plan</h2>
          <p className="text-gos-slate mt-3">
            Answer a few quick questions. You'll receive an immediate confirmation and our
            team will review your requirements for the next steps.
          </p>

          {/* Progress */}
          <div className="mt-8 flex items-center gap-3 text-xs text-gos-muted">
            <span className={step >= 1 ? "text-gos-navy font-semibold" : ""}>1. About you</span>
            <span className="flex-1 h-[2px] bg-gos-border relative"><span style={{ width: `${(step - 1) * 50}%` }} className="absolute inset-y-0 left-0 bg-gos-maple transition-all" /></span>
            <span className={step >= 2 ? "text-gos-navy font-semibold" : ""}>2. Goals</span>
            <span className="flex-1 h-[2px] bg-gos-border relative"><span style={{ width: `${Math.max(0, (step - 2)) * 100}%` }} className="absolute inset-y-0 left-0 bg-gos-maple transition-all" /></span>
            <span className={step >= 3 ? "text-gos-navy font-semibold" : ""}>3. Services</span>
          </div>

          <div className="mt-6 bg-white border border-gos-border rounded-sm p-6 md:p-8">
            {step === 1 && (
              <div className="space-y-4" data-testid="lp-form-step-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Full name *" value={form.full_name} onChange={(v) => setF("full_name", v)} testid="lp-f-name" />
                  <F label="Email *" type="email" value={form.email} onChange={(v) => setF("email", v)} testid="lp-f-email" />
                  <F label="WhatsApp number *" value={form.whatsapp} onChange={(v) => setF("whatsapp", v)} placeholder="+91…" testid="lp-f-whatsapp" />
                  <F label="City / State in India *" value={form.city_state} onChange={(v) => setF("city_state", v)} placeholder="e.g. Mumbai, Maharashtra" testid="lp-f-city" />
                  <div className="md:col-span-2">
                    <F label="Current business or occupation" value={form.current_occupation} onChange={(v) => setF("current_occupation", v)} placeholder="e.g. Existing Business Owner · SaaS Founder · Consultant" testid="lp-f-occ" />
                  </div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-6" data-testid="lp-form-step-2">
                <RadioGroup label="What do you want to do in Canada? *" value={form.intent} onChange={(v) => setF("intent", v)} options={[
                  "Start a new company", "Expand my existing Indian company", "Open a Canadian branch",
                  "Explore Canadian business opportunities", "Not sure yet",
                ]} testidPrefix="lp-intent" />
                <RadioGroup label="When do you want to start? *" value={form.timeline} onChange={(v) => setF("timeline", v)} options={[
                  "Immediately", "Within 30 days", "1-3 months", "3-6 months", "Just researching",
                ]} testidPrefix="lp-timeline" />
                <RadioGroup label="Approximate available setup budget *" value={form.budget_range} onChange={(v) => setF("budget_range", v)} options={[
                  "Under CAD $2,500", "CAD $2,500-$5,000", "CAD $5,000-$10,000", "CAD $10,000+", "Not decided",
                ]} testidPrefix="lp-budget" />
              </div>
            )}
            {step === 3 && (
              <div className="space-y-6" data-testid="lp-form-step-3">
                <div>
                  <label className="block text-sm font-semibold text-gos-charcoal mb-3">Which services are you interested in? (select all)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {QUALIFICATION_SERVICES.map((s) => {
                      const on = form.service_interest.includes(s);
                      return (
                        <button
                          key={s} type="button" onClick={() => toggleService(s)}
                          data-testid={`lp-svc-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`text-left p-3 border rounded-sm text-sm flex items-start gap-2 transition-colors ${on ? "bg-gos-navy text-white border-gos-navy" : "bg-white border-gos-border hover:border-gos-navy"}`}
                        >
                          {on
                            ? <CheckCircle size={16} weight="fill" className="text-gos-maple mt-0.5" />
                            : <Circle size={16} className="text-gos-muted mt-0.5" />}
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="flex items-start gap-3 text-xs text-gos-slate cursor-pointer">
                  <input
                    type="checkbox" checked={form.marketing_consent} onChange={(e) => setF("marketing_consent", e.target.checked)}
                    className="mt-0.5" data-testid="lp-consent"
                  />
                  <span>
                    I consent to receive follow-up communication (email, WhatsApp, phone) from
                    Open Business Anywhere regarding my enquiry. I can unsubscribe at any time.
                  </span>
                </label>
                {error && <div className="text-xs text-gos-maple bg-gos-maple/10 border border-gos-maple/40 rounded-sm p-3">{error}</div>}
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
              className="btn-secondary disabled:opacity-40" data-testid="lp-form-back">← Back</button>
            {step < 3 ? (
              <button type="button"
                disabled={step === 1 ? !canGoStep2 : !canGoStep3}
                onClick={() => { trackConversion("lp_form_step", { value: step, payload: { step } }); setStep((s) => s + 1); }}
                className="btn-primary disabled:opacity-40" data-testid="lp-form-next">
                Continue <ArrowRight size={14} weight="bold" />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="lp-form-submit">
                {submitting ? "Submitting…" : (<>Submit <ArrowRight size={14} weight="bold" /></>)}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── helpers ─── */
function F({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <input
        type={type} value={value ?? ""} placeholder={placeholder} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gos-navy"
      />
    </div>
  );
}

function RadioGroup({ label, value, onChange, options, testidPrefix }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gos-charcoal mb-3">{label}</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {options.map((opt) => {
          const on = value === opt;
          return (
            <button
              key={opt} type="button" onClick={() => onChange(opt)}
              data-testid={`${testidPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`text-left p-3 border rounded-sm text-sm flex items-start gap-2 transition-colors ${on ? "bg-gos-navy text-white border-gos-navy" : "bg-white border-gos-border hover:border-gos-navy"}`}
            >
              {on ? <CheckCircle size={16} weight="fill" className="text-gos-maple mt-0.5" /> : <Circle size={16} className="text-gos-muted mt-0.5" />}
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
